/**
 * «Борлуулалт, ашиг» — тайлан хуудасны шүүлтүүртэй блок.
 *
 * Шүүлтүүр → гол үзүүлэлт (өмнөх «мөн үе»-тэй харьцуулсан) → салбараар.
 * Шүүлтүүр ЗӨВХӨН энэ блокт үйлчилнэ; хуудасны бусад хэсэг хугацаанаас
 * хамаарахгүй тул тусдаа гарчигтай.
 *
 * ⚠️ Юу ч ТООЦООЛОХГҮЙ — дүн бүр `/reports/sales`-ийн `totals`-оос.
 *    Өртөг, ашиг, урамшууллыг эрхгүй хүнд сервер `null` буцаана.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { changePercent, formatMntShort, formatQty, type ReportPreset } from '@boots/domain';
import { api, type Envelope } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { COST_ROLES, type SalesReport } from '../../lib/types';
import { salesQuery, useReportFilter } from '../../lib/use-report-filter';
import { Section } from '../section';
import { StatCard, type StatDelta } from '../stat-card';
import { ErrorNote, Spinner } from '../ui';
import { SalesFilters } from './sales-filters';
import { StoreSales } from './store-sales';

const PREV_LABEL: Record<ReportPreset, string> = {
  today: 'өчигдрийн мөн цагаас',
  yesterday: 'уржигдраас',
  month: 'өмнөх сарын мөн үеэс',
  'prev-month': 'түүний өмнөх сараас',
  year: 'өнгөрсөн оны мөн үеэс',
  custom: 'өмнөх ижил хугацаанаас',
};

type Totals = SalesReport['totals'];

/**
 * Маржин ба өртөггүй гутлын сэрэмжлүүлэг. Ханш тооцоонд орохгүй болсноос
 * хойш (дүрэм 4) олон гутал ₮ өртөггүй зарагддаг — ашигт ОРООГҮЙ гэдгийг
 * хэлэхгүй бол ашиг бодитоосоо бага мэт уншигдана. Өмнө нь зөвхөн
 * Санхүүгийн нуусан картад байв (2026-09-29).
 */
function profitNote(t: Totals) {
  const parts = [
    t.marginPct ? `маржин ${t.marginPct}%` : null,
    t.uncostedUnits > 0 ? `${formatQty(t.uncostedUnits)} ш өртөггүй — ашигт ороогүй` : null,
  ].filter(Boolean);
  return parts.length ? <span className="text-meta text-ink-2">{parts.join(' · ')}</span> : undefined;
}

export function SalesBlock() {
  const { can } = useAuth();
  const showCost = can(...COST_ROLES);
  const f = useReportFilter();
  const r = f.range;

  const load = (from?: Date, to?: Date) => {
    const path = from && to ? `/reports/sales?groupBy=store&${salesQuery(from, to, f.storeId)}` : '';
    return {
      queryKey: ['sales-report', 'range', path],
      queryFn: () => api<Envelope<SalesReport>>(path),
      enabled: path !== '',
      // Шүүлтүүр солиход өмнөх дүн бүдгэрч үлдэнэ — хоосон анивчихгүй
      placeholderData: keepPreviousData,
    };
  };
  const cur = useQuery(load(r?.from, r?.to));
  const prev = useQuery(load(r?.prevFrom, r?.prevTo));

  const delta = (pick: (t: Totals) => string | null, upIsGood: boolean): StatDelta | undefined => {
    // Шүүлтүүр солигдож байх үед хуучин мужийн дүнтэй харьцуулахгүй
    if (cur.isPlaceholderData || prev.isPlaceholderData) return undefined;
    const c = cur.data && pick(cur.data.data.totals);
    const p = prev.data && pick(prev.data.data.totals);
    if (c == null || p == null) return undefined;
    const pct = changePercent(c, p);
    if (pct === null) return { text: 'харьцуулах өгөгдөл алга' };
    return {
      text: `${Math.abs(pct)}% ${PREV_LABEL[f.preset]}`,
      direction: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat',
      good: upIsGood ? pct >= 0 : undefined,
    };
  };

  const t = cur.data?.data.totals;
  const money = (v: string | null | undefined) => (!showCost ? '•••' : v == null ? '—' : formatMntShort(v));

  return (
    <Section title="Борлуулалт, ашиг" note="буцаагдсан, хүчингүй болсныг хассан цэвэр дүн">
      <SalesFilters f={f} />

      {!r ? (
        <p className="text-[12.5px] text-ink-3">Эхлэх, дуусах огноог сонгоно уу</p>
      ) : cur.error ? (
        <ErrorNote>{(cur.error as Error).message}</ErrorNote>
      ) : !t ? (
        <Spinner />
      ) : (
        <div className={`flex flex-col gap-4 transition ${cur.isPlaceholderData ? 'opacity-60' : ''}`}>
          {/*
            ⚠️ «Урамшуулал» картыг НУУВ (2026-10-05, захиалагчийн цэгцлэл №16):
               урамшуулал бодитоор олгогдож буй эсэх тодорхойгүй. Тооцоо нь
               ХЭВЭЭР — `order.confirm` хувийг хөлдөөж, `t.commissionMnt`
               ирсээр байна. Шаардлага тодорхой болвол картыг буцаана.
          */}
          <div className="grid grid-cols-2 items-stretch gap-3 xl:grid-cols-4">
            <StatCard label="Орлого" icon="coins" value={formatMntShort(t.revenueMnt)} delta={delta((x) => x.revenueMnt, true)} />
            <StatCard label="Зарсан" icon="box" value={`${formatQty(t.units)} ш`} delta={delta((x) => String(x.units), true)} />
            <StatCard
              label="Ашиг"
              icon="chart"
              value={money(t.profitMnt)}
              delta={showCost ? delta((x) => x.profitMnt, true) : undefined}
              pending={!showCost}
              footer={showCost ? profitNote(t) : undefined}
            />
            {/* «Зарлага» (Санхүү — аялалд зарцуулсан)-аас ялгахын тулд бүтэн нэрээр */}
            <StatCard label="Зарсан гутлын өртөг" icon="receipt" value={money(t.costMnt)} delta={showCost ? delta((x) => x.costMnt, false) : undefined} pending={!showCost} />          </div>

          <div className="flex flex-col gap-2.5 border-t border-hair pt-3.5">
            <span className="text-meta text-ink-2">Салбараар</span>
            <StoreSales groups={cur.data?.data.groups ?? []} />
          </div>
        </div>
      )}
    </Section>
  );
}
